import {
	Button,
	Checkbox,
	FormControlLabel,
	MuiThemeProvider,
	TextField,
	Tooltip,
	createMuiTheme,
} from "@material-ui/core";
import { Delete, Visibility } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import { SERVER_URL } from "../../../../api";
import {
	deleteEventType,
	getEventTypeById,
	postEventType,
	updateEventType,
} from "../../../../api/eventType";
import { checkIsEmpty } from "../../../../utils/helpers";
import { alertError, alertSuccess } from "../../../../utils/logger";
import MultilanguageTabBlock from "../../../components/MultilanguageTabBlock";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import PreviewDialog from "../../../components/dialogs/PreviewDialog";
import { buttonsStyle } from "../../../components/tables/table";
import { useSkeleton } from "../../../hooks/useSkeleton";

// Create theme for delete button (red)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#F64E60",
		},
	},
});

function getEmptyEventType() {
	return {
		title: {},
		iconURL: null,
		active: true,
	};
}

export default function EditEventTypesPage() {
	const [eventType, setEventType] = useState(getEmptyEventType());
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const [selectedImage, setSelectedImage] = useState(null);
	const [openPreviewImage, setOpenPreviewImage] = useState(false);

	const eventTypeId = useParams().id;
	const history = useHistory();
	const user = useSelector(
		(store) => store.authentication?.user,
		shallowEqual
	);

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		if (!eventTypeId) {
			disableLoadingData();
			return;
		}
		getEventTypeById(eventTypeId)
			.then((res) => {
				if (res.status === 200) {
					setEventType(res.data);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "No se pudo obtener el tipo de evento.",
				});
				history.push("/event-types");
			});
	}, [eventTypeId, disableLoadingData, history]);

	function saveEventType() {
		if (checkIsEmpty(eventType.title))
			return alertError({
				error: null,
				customMessage:
					"El título es necesario en alguno de los idiomas.",
			});
		if (!eventTypeId) {
			postEventType(eventType, selectedImage)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({
							title: "Saved!",
							customMessage: "Tipo de evento creado con éxito.",
						});
						history.push("/event-types");
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save event type.",
					});
				});
		} else {
			updateEventType(eventTypeId, eventType, selectedImage)
				.then((res) => {
					if (res.status === 200) {
						alertSuccess({
							title: "Saved!",
							customMessage: "Changes successfully saved.",
						});
						history.push("/event-types");
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save changes.",
					});
				});
		}
	}

	const handleChange = (element, lang) => (event) => {
		if (event.target.value === " ") return;
		if (lang) {
			if (!eventType[element]) eventType[element] = {};
			let newText = eventType[element];
			newText[lang] = event.target.value;
			setEventType({ ...eventType, [element]: newText });
		} else
			setEventType({
				...eventType,
				[element]: event.target.value,
			});
	};

	const renderMultilanguageTabContent = (lang) => {
		return (
			<>
				<br />
				<TextField
					id={`title`}
					label="Title"
					value={(eventType.title && eventType.title[lang]) || ""}
					onChange={handleChange("title", lang)}
					InputLabelProps={{
						shrink: true,
					}}
					margin="normal"
					variant="outlined"
					required
				/>
			</>
		);
	};

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
					<CardHeader title="Editar tipo de evento"></CardHeader>
					<CardBody>
						<MultilanguageTabBlock
							multilanguageTabContent={
								renderMultilanguageTabContent
							}
						/>
						<br />
						<label htmlFor={"upload-image"}>
							<input
								style={{ display: "none" }}
								id={"upload-image"}
								name={"upload-image"}
								type="file"
								accept={"image/*"}
								onChange={(e) => {
									setSelectedImage(e.target.files[0]);
								}}
							/>
							<Button
								style={{ marginRight: "15px" }}
								color="secondary"
								component="span"
								variant="outlined"
							>
								{selectedImage || eventType.iconURL
									? "Change image"
									: "Upload image"}
							</Button>
						</label>
						{(selectedImage || eventType.iconURL) && (
							<>
								<Tooltip title={"Preview image"}>
									<Button
										size="small"
										onClick={() =>
											setOpenPreviewImage(true)
										}
										style={{
											...buttonsStyle,
										}}
									>
										<Visibility />
									</Button>
								</Tooltip>
								<Tooltip title="Delete">
									<Button
										size="small"
										style={{
											...buttonsStyle,
											marginRight: "15px",
										}}
										onClick={() => {
											setSelectedImage(null);
											setEventType({
												...eventType,
												iconURL: null,
											});
										}}
									>
										<Delete />
									</Button>
								</Tooltip>
								<PreviewDialog
									title={"Preview image"}
									open={openPreviewImage}
									setOpen={setOpenPreviewImage}
									src={
										selectedImage
											? URL.createObjectURL(selectedImage)
											: `${SERVER_URL}/${eventType.iconURL}`
									}
								/>
								<span>
									{selectedImage
										? selectedImage?.name
										: eventType.iconURL
										? eventType.iconURL.split(/-(.*)/s)[1]
										: ""}
								</span>
							</>
						)}
						<br />
						<br />
						<FormControlLabel
							control={
								<Checkbox
									checked={eventType.active}
									onChange={() =>
										setEventType({
											...eventType,
											active: !eventType.active,
										})
									}
									name="checkActive"
								/>
							}
							label="Active"
						/>
					</CardBody>
				</Card>
				<div style={{ display: "flex", flexDirection: "row" }}>
					<Button
						onClick={() => history.push("/event-types")}
						variant="outlined"
						style={{ marginRight: "20px" }}
					>
						Back
					</Button>
					<Button
						onClick={() => saveEventType()}
						variant="outlined"
						color="primary"
						style={{ marginRight: "20px" }}
					>
						Save event type
					</Button>
					{eventTypeId && user?.role === "admin" && (
						<>
							<MuiThemeProvider theme={theme}>
								<Button
									onClick={() => setOpenConfirmDialog(true)}
									variant="outlined"
									color="secondary"
									style={{ marginRight: "20px" }}
								>
									Delete event type
								</Button>
								<div
									style={{
										display: "flex",
										flexDirection: "row",
										marginLeft: "auto",
									}}
								></div>
							</MuiThemeProvider>

							<ConfirmDialog
								title={
									"Are you sure you want to delete this event type?"
								}
								open={openConfirmDialog}
								setOpen={setOpenConfirmDialog}
								onConfirm={() => {
									deleteEventType(eventTypeId)
										.then((res) => {
											if (
												res.status === 204 ||
												res.status === 200
											) {
												alertSuccess({
													title: "Deleted!",
													customMessage:
														"Event type deleted successfully",
												});
												history.push("/event-types");
											}
										})
										.catch((error) => {
											alertError({
												error: error,
												customMessage:
													"Could not delete event type.",
											});
										});
								}}
							/>
						</>
					)}
				</div>
			</>
		);
}
